import type { UseFormRegisterReturn } from 'react-hook-form'
import { z } from 'zod'
import { hasControlCharacters } from '@speaking-coach/shared-types/src/text-controls.js'
import { Badge, FieldError, FieldLabel, Input } from '@speaking-coach/ui'
import { tooLong } from '../common/length-message'

export const companyInformationShape = {
  clientEmployeeId: singleLineCompanyValue(100),
  clientDepartment: singleLineCompanyValue(200),
}

function singleLineCompanyValue(limit: number) {
  return z.string().trim().max(limit, tooLong(limit)).refine(
    (value) => !hasControlCharacters(value),
    'Use a single line without control characters.',
  ).optional()
}

export function CompanyInformationFields({
  employee, department, employeeError, departmentError,
  employeeDisabled = false, departmentDisabled = false, showAvailability = false,
}: {
  employee?: UseFormRegisterReturn
  department?: UseFormRegisterReturn
  employeeError?: string
  departmentError?: string
  employeeDisabled?: boolean
  departmentDisabled?: boolean
  showAvailability?: boolean
}): JSX.Element {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {employee && (
        <div>
          <div className="flex items-center gap-2">
            <FieldLabel htmlFor="client-employee-id" label="Employee ID" help="Optional reference from the client's employee records. Leading zeros are preserved." />
            {showAvailability && <Badge id="employee-availability" className="mb-1 ml-auto shrink-0" variant={employeeDisabled ? 'muted' : 'neutral'}>{employeeDisabled ? 'Disabled' : 'Enabled'}</Badge>}
          </div>
          <Input id="client-employee-id" maxLength={100} {...employee} disabled={employeeDisabled} aria-describedby={showAvailability ? 'employee-availability' : undefined} />
          <FieldError message={employeeError ?? null} />
        </div>
      )}
      {department && (
        <div>
          <div className="flex items-center gap-2">
            <FieldLabel htmlFor="client-department" label="Department" help="Optional current department in the client's company. Applies across all study periods." />
            {showAvailability && <Badge id="department-availability" className="mb-1 ml-auto shrink-0" variant={departmentDisabled ? 'muted' : 'neutral'}>{departmentDisabled ? 'Disabled' : 'Enabled'}</Badge>}
          </div>
          <Input id="client-department" maxLength={200} {...department} disabled={departmentDisabled} aria-describedby={showAvailability ? 'department-availability' : undefined} />
          <FieldError message={departmentError ?? null} />
        </div>
      )}
    </div>
  )
}
